import DialogCustom from 'src/components/elements/DialogCustom/DialogCustom'
import {NewEnrollmentDialogStyled} from './NewEnrollmentDialogStyled'
import {FormattedMessage} from 'react-intl'
import {Form, Input, Select, DatePicker, TimePicker, InputNumber} from 'antd'
import PrimaryButton from 'src/components/elements/Buttons/PrimaryButton/PrimaryButton'
import SecondaryButton from 'src/components/elements/Buttons/SecondaryButton/SecondaryButton'
import {useForm} from 'antd/lib/form/Form'
import UnitSelector from 'src/components/elements/UnitSelector'
import {THERAPY_DAILY_DOSAGE_UNIT_TYPE_OPTIONS, THERAPY_DOSAGE_UNIT_TYPE_OPTIONS, THERAPY_SPEED_OF_ASSUMPTION_UNIT_OPTIONS, THERAPY_SPEED_OF_DEVICE_UNIT_OPTIONS} from 'src/constants/constants'
import {useEffect, useState} from 'react'
import apiClient from 'src/services/apiClient'
import {useAppSelector} from 'src/hooks'
import locale from 'antd/es/date-picker/locale/it_IT'
import moment from 'moment'

type NewEnrollmentDialogProps = {
  therapyTypeOptions: {value: string, label: string}[];
  serviceTypesOptions: {value: string, label: string}[];
  request?: Record<string, any>;
  onSubmit: (values: any) => void;
  onClose: () => void;
};

const NewEnrollmentDialog: React.FC<NewEnrollmentDialogProps> = ({therapyTypeOptions, serviceTypesOptions, request, onSubmit, onClose}) => {
  const {serviceTypeRequired, showServiceType, customFields, showDosage, showDailyDosage, showDeviceSpeed, showAdministrationSpeed} = useAppSelector(state => state.pspConfigReducer.data.enrollmentHubConfigurations?.digitalEnrollmentConfigurations ?? {})

  const [form] = useForm()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [status, setStatus] = useState<any>(undefined)

  useEffect(() => {
    form.resetFields()
    if (request) {
      form.setFieldsValue(request)
      form.setFieldValue('therapyTypeId', request.therapyType?._id)

      if (request.customFields && customFields) {
        customFields.forEach((field: any) => {
          const customFieldData = request.customFields.find((cf: any) => cf.id === field.id)
          if (customFieldData && customFieldData.value) {
            let value = customFieldData.value

            if (field.type === 'date') {
              value = moment(customFieldData.value, ['DD/MM/YYYY', moment.ISO_8601], true)
            } else if (field.type === 'time') {
              value = moment(customFieldData.value, ['HH:mm', moment.ISO_8601], true)
            }

            form.setFieldValue(field.id, value)
          }
        })
      }
    }
  }, [form, request, customFields])

  const onFinish = (values: any) => {
    const customFieldsData = customFields?.map((field: any) => {
      const value = values[field.id]

      return {
        id: field.id,
        value,
        label: field.label,
        type: field.type,
        ...(field.unit && {unitType: field.unit})
      }
    }).filter((field: any) => field.value !== undefined && field.value !== null && field.value !== '') || []

    const customFieldIds = customFields?.map((field: any) => field.id) || []
    const filteredValues = Object.keys(values).reduce<Record<string, any>>((acc, key) => {
      if (!customFieldIds.includes(key)) {
        acc[key] = values[key]
      }
      return acc
    }, {})

    const body = {
      ...filteredValues,
      drugDosage: values?.drugDosage?.value ? {value: values.drugDosage.value, unitType: values.drugDosage.unitType ?? THERAPY_DOSAGE_UNIT_TYPE_OPTIONS[0].value} : undefined,
      dailyDosage: values?.dailyDosage?.value ? {value: values.dailyDosage.value, unitType: values.dailyDosage.unitType ?? THERAPY_DAILY_DOSAGE_UNIT_TYPE_OPTIONS[0].value} : undefined,
      speedOfAssumption: values?.speedOfAssumption?.value ? {value: values.speedOfAssumption.value, unitType: values.speedOfAssumption.unitType ?? THERAPY_SPEED_OF_ASSUMPTION_UNIT_OPTIONS[0].value} : undefined,
      speedOfDevice: values?.speedOfDevice?.value ? {value: values.speedOfDevice.value, unitType: values.speedOfDevice.unitType ?? THERAPY_SPEED_OF_DEVICE_UNIT_OPTIONS[0].value} : undefined,
      customFields: customFieldsData
    }
    onSubmit(body)
  }

  const body = (
    <NewEnrollmentDialogStyled>
      <Form
        form={form}
        id='digital-enrollment-form'
        layout='vertical'
        onFinish={onFinish}
      >
        <Form.Item
          className='firstName'
          label='Nome Paziente'
          name='firstName'
          rules={[{required: true, message: 'Campo obbligatorio'}]}
        >
          <Input disabled={Boolean(request)} />
        </Form.Item>
        <Form.Item
          className='lastName'
          label='Cognome Paziente'
          name='lastName'
          rules={[{required: true, message: 'Campo obbligatorio'}]}
        >
          <Input disabled={Boolean(request)} />
        </Form.Item>
        <Form.Item
          className='email'
          label='Email'
          name='email'
          rules={[
            {type: 'email', message: 'Formato email non valido'},
            () => ({
              async validator (_, value) {
                const isEmailValid = await apiClient.checkEmail(value, setStatus)
                if (isEmailValid) {
                  return Promise.resolve()
                }
                return Promise.reject(new Error('Email non disponibile'))
              }
            }),
            ({getFieldValue}) => ({
              validator (_, value) {
                if (value || getFieldValue('cellPhoneNumber')) {
                  return Promise.resolve()
                }
                return Promise.reject(new Error('Un input tra Email e Numero cellulare deve essere compilato'))
              }
            })
          ]}
        >
          <Input disabled={Boolean(request)} onChange={() => form.validateFields()} />
        </Form.Item>
        <Form.Item
          className='cellPhoneNumber'
          label='Numero cellulare'
          name='cellPhoneNumber'
          rules={[
            ({getFieldValue}) => ({
              validator (_, value) {
                if (value || getFieldValue('email')) {
                  return Promise.resolve()
                }
                return Promise.reject(new Error('Un input tra Email e Numero cellulare deve essere compilato'))
              }
            })
          ]}
        >
          <Input disabled={Boolean(request)} onChange={() => form.validateFields()} />
        </Form.Item>
        {showServiceType &&
          <Form.Item
            className='serviceType'
            label='Tipologia servizio'
            name='serviceType'
            rules={serviceTypeRequired ? [{required: true, message: 'Campo obbligatorio'}] : []}
          >
            <Select
              disabled={Boolean(request)}
              getPopupContainer={(trigger) => trigger}
              options={serviceTypesOptions}
            />
          </Form.Item>}
        <Form.Item
          className='therapyTypeId'
          label='Terapia'
          name='therapyTypeId'
          rules={[{required: true, message: 'Campo obbligatorio'}]}
        >
          <Select
            disabled={Boolean(request)}
            getPopupContainer={(trigger) => trigger}
            options={therapyTypeOptions}
          />
        </Form.Item>
        {showDosage &&
          <Form.Item
            className='drugDosage'
            label='Dosaggio'
            name='drugDosage'
          >
            <UnitSelector
              disabled={Boolean(request)}
              onChange={(values) => {}}
              unitTypeOptions={THERAPY_DOSAGE_UNIT_TYPE_OPTIONS}
              value={request?.drugDosage}
            />
          </Form.Item>}
        {showDailyDosage &&
          <Form.Item
            className='dailyDosage'
            label='Dosaggio giornaliero'
            name='dailyDosage'
          >
            <UnitSelector
              disabled={Boolean(request)}
              onChange={(values) => {}}
              unitTypeOptions={THERAPY_DAILY_DOSAGE_UNIT_TYPE_OPTIONS}
              value={request?.dailyDosage}
            />
          </Form.Item>}
        {showAdministrationSpeed &&
          <Form.Item
            className='speedOfAssumption'
            label='Velocità di somministrazione'
            name='speedOfAssumption'
          >
            <UnitSelector
              disabled={Boolean(request)}
              onChange={(values) => {}}
              unitTypeOptions={THERAPY_SPEED_OF_ASSUMPTION_UNIT_OPTIONS}
              value={request?.speedOfAssumption}
            />
          </Form.Item>}
        {showDeviceSpeed &&
          <Form.Item
            className='speedOfDevice'
            label='Velocità device'
            name='speedOfDevice'
          >
            <UnitSelector
              disabled={Boolean(request)}
              onChange={(values) => {}}
              unitTypeOptions={THERAPY_SPEED_OF_DEVICE_UNIT_OPTIONS}
              value={request?.speedOfDevice}
            />
          </Form.Item>}
        {customFields?.map((field: any) => {
          const renderField = () => {
            switch (field.type) {
              case 'string':
                return (
                  <Input
                    disabled={Boolean(request)}
                  />
                )

              case 'textarea':
                return (
                  <Input.TextArea
                    disabled={Boolean(request)}
                    rows={4}
                  />
                )

              case 'number':
                return (
                  <InputNumber
                    addonAfter={field.unit}
                    disabled={Boolean(request)}
                    style={{width: '100%'}}
                  />
                )

              case 'date':
                return (
                  <DatePicker
                    disabled={Boolean(request)}
                    format='DD/MM/YYYY'
                    getPopupContainer={(trigger) => trigger}
                    locale={locale}
                  />
                )

              case 'time':
                return (
                  <TimePicker
                    disabled={Boolean(request)}
                    format='HH:mm'
                    getPopupContainer={(trigger) => trigger}
                    locale={locale}
                    placeholder=''
                  />
                )

              case 'enum':
                return (
                  <Select
                    disabled={Boolean(request)}
                    getPopupContainer={(trigger) => trigger}
                    mode={field.multiSelect ? 'multiple' : undefined}
                    options={field.options?.map((opt: string) => ({label: opt, value: opt})) || []}
                  />
                )

              default:
                return (
                  <Input
                    disabled={Boolean(request)}
                  />
                )
            }
          }

          return (
            <Form.Item
              className={field.id}
              key={field.id}
              label={field.label}
              name={field.id}
              rules={field.required ? [{required: true, message: 'Campo obbligatorio'}] : []}
            >
              {renderField()}
            </Form.Item>
          )
        })}
      </Form>
    </NewEnrollmentDialogStyled>
  )

  const footer = (
    <div style={{display: 'flex', width: '100%'}}>
      <PrimaryButton
        form='digital-enrollment-form'
        key='submit'
        type='submit'
      >
        <FormattedMessage id='save' />
      </PrimaryButton>
      <SecondaryButton onClick={onClose}>
        <FormattedMessage id='undo' />
      </SecondaryButton>
    </div>
  )

  return (
    <DialogCustom
      body={body}
      footer={footer}
      maxWidth='lg'
      onClose={onClose}
      title={{text: 'Attivazione paziente', format: false}}
    />
  )
}

export default NewEnrollmentDialog
